<script lang="ts">
  import { RefreshCw, Settings } from "@lucide/svelte";
  import { MODES, SKINS, presenceSections, recentEndedMeetings, type Mode, type RadarMeeting, type RadarPerson, type RadarRefreshState, type Skin } from "../model";
  import AppearanceControls from "./AppearanceControls.svelte";
  import AvatarCluster from "./AvatarCluster.svelte";
  import RadarArtwork from "./RadarArtwork.svelte";
  import SectionLabel from "./SectionLabel.svelte";
  import SessionRow from "./SessionRow.svelte";

  let { meetings, people = [], now, mode, skin, connection = "connected", refreshState = "idle", buildGitCommit, minimal = false, showWaiting = false, onModeChange, onSkinChange, onRefresh, onSettings }: {
    meetings: RadarMeeting[]; people?: RadarPerson[]; now: Date; mode: Mode; skin: Skin; connection?: "connected" | "reconnecting" | "offline";
    refreshState?: RadarRefreshState; buildGitCommit?: string; minimal?: boolean; showWaiting?: boolean;
    onModeChange?: (mode: Mode) => void; onSkinChange?: (skin: Skin) => void; onRefresh?: () => void; onSettings?: () => void;
  } = $props();
  let expandedId = $state<string | null>(null);
  let live = $derived(meetings.filter((meeting) => meeting.status === "live"));
  let waiting = $derived(meetings.filter((meeting) => meeting.status === "waiting"));
  let ended = $derived(recentEndedMeetings(meetings, now));
  let presence = $derived(presenceSections(people, meetings));
  let tokens = $derived(MODES[mode]);
  let colors = $derived(SKINS[skin]);
</script>

<div class="radar-wrap" data-mode={mode} style={`--page:${tokens.bg};--surface:${tokens.surface};--toolbar:${tokens.toolbar};--heading-from:${tokens.headingFrom};--heading-to:${tokens.headingTo};--text:${tokens.text};--muted:${tokens.muted};--dim:${tokens.dim};--readable-muted:${tokens.readableMuted};--readable-dim:${tokens.readableDim};--border:${tokens.border};--hover:${tokens.hover};--accent:${colors.accent};--waiting:${colors.waiting};--glow:${colors.glow}`}>
  <article class:minimal class="meeting-radar" aria-label="Meeting Radar">
    <header class="radar-toolbar">
      <AppearanceControls {mode} {skin} {onModeChange} {onSkinChange} />
      {#if connection !== "connected"}<span class:offline={connection === "offline"} class="connection-status" role="status">{connection === "offline" ? "Offline" : "Reconnecting"}</span>{/if}
    </header>
    <section class="radar-heading">
      <RadarArtwork {now} />
      <h1>Meeting Radar</h1>
      <p><time datetime={now.toISOString()}>{now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} · {now.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" })}</time></p>
    </section>

    {#if !minimal}
      <SectionLabel label="Happening Now" count={live.length} accent={colors.accent} textColor={mode === "light" ? "#f5f5fa" : "#0d0d0f"} first />
      <div class="session-group bordered live-group">
        {#each live as meeting (meeting.id)}<SessionRow {meeting} {now} expanded={expandedId === meeting.id} onToggle={() => expandedId = expandedId === meeting.id ? null : meeting.id} />{/each}
        {#if live.length === 0}<p class="empty-row">No meetings happening now</p>{/if}
      </div>
      {#if showWaiting}
        <SectionLabel label="Waiting" count={waiting.length} accent={colors.waiting} textColor={mode === "dark" ? "#0d0d0f" : "#ffffff"} />
        <div class="session-group bordered waiting-group">
          {#each waiting as meeting (meeting.id)}<SessionRow {meeting} {now} expanded={expandedId === meeting.id} onToggle={() => expandedId = expandedId === meeting.id ? null : meeting.id} />{/each}
          {#if waiting.length === 0}<p class="empty-row">No one is waiting</p>{/if}
        </div>
      {/if}
      {#if !showWaiting}
        <SectionLabel label="Available" count={presence.available.length} accent={colors.waiting} textColor={mode === "dark" ? "#0d0d0f" : "#ffffff"} />
        <div class="presence-group bordered available-group">
          {#if presence.available.length > 0}<AvatarCluster people={presence.available} id="available" maxVisible={10} label="Available people" />{/if}
          {#if presence.available.length === 0}<p class="empty-row">No one is available</p>{/if}
        </div>
        <SectionLabel label="Not Available" count={presence.notAvailable.length} accent={tokens.dim} textColor={tokens.muted} />
        <div class="presence-group bordered unavailable-group">
          {#if presence.notAvailable.length > 0}<AvatarCluster people={presence.notAvailable} id="not-available" maxVisible={10} muted label="Not available people" />{/if}
          {#if presence.notAvailable.length === 0}<p class="empty-row">No one is unavailable</p>{/if}
        </div>
      {/if}
      <SectionLabel label="Recently Ended" count={ended.length} accent={tokens.dim} textColor={tokens.muted} />
      <div class="session-group final">
        {#each ended as meeting (meeting.id)}<SessionRow {meeting} {now} expanded={expandedId === meeting.id} onToggle={() => expandedId = expandedId === meeting.id ? null : meeting.id} />{/each}
        {#if ended.length === 0}<p class="empty-row">No recent meetings</p>{/if}
      </div>
    {/if}
  </article>
  {#if !minimal}
    <div class="radar-actions">
      {#if onRefresh}
        {#if buildGitCommit}<span class="build-commit" aria-label={`Build commit ${buildGitCommit}`} title={`Build commit ${buildGitCommit}`}>{buildGitCommit.slice(0, 5)}</span>{/if}
        <button
          class:error={refreshState === "error"}
          class:loading={refreshState === "loading"}
          class="refresh-link"
          type="button"
          aria-label={refreshState === "loading" ? "Refreshing radar" : refreshState === "error" ? "Refresh failed. Try again" : "Refresh radar"}
          aria-busy={refreshState === "loading"}
          title={refreshState === "loading" ? "Refreshing" : refreshState === "error" ? "Refresh failed. Try again" : "Refresh"}
          disabled={refreshState === "loading"}
          onclick={() => onRefresh()}
        ><RefreshCw size={13} /></button>
      {/if}
      <button class="settings-link" type="button" aria-label="Open settings" title="Settings" onclick={() => onSettings?.()}><Settings size={13} /></button>
    </div>
  {/if}
</div>
